import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import SearchSelect from "@/components/common/SearchSelect";
import { fmtINR, fmtDate, today } from "@/lib/format";
import { toast } from "sonner";
import { Download, Mail, FileText } from "lucide-react";
import { API } from "@/lib/api";

function StatementForType({ partyType, label }) {
  const [parties, setParties] = useState([]);
  const [partyId, setPartyId] = useState("");
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 3);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(today());
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    api.get("/parties", { params: partyType === "other" ? {} : { party_type: partyType } })
      .then((r) => {
        // For "Other", we exclude customer + vendor
        const list = partyType === "other"
          ? r.data.filter((p) => !["customer", "vendor", "supplier"].includes(p.party_type))
          : r.data;
        setParties(list);
      });
  }, [partyType]);

  const run = async () => {
    if (!partyId) return toast.error("Select a party");
    setLoading(true);
    try {
      const r = await api.get("/reports/statement-of-account", {
        params: { party_id: partyId, from_date: fromDate, to_date: toDate },
      });
      setStatement(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const downloadPDF = async () => {
    if (!partyId) return;
    const url = `${API}/reports/statement-of-account.pdf?party_id=${partyId}&from_date=${fromDate}&to_date=${toDate}`;
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) return toast.error("PDF failed");
    const blob = await res.blob(); const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = `Statement_${partyType}_${fromDate}_to_${toDate}.pdf`; a.click();
    URL.revokeObjectURL(u);
  };

  const partyOptions = useMemo(
    () => parties.map((p) => ({ value: p.id, label: p.name, sublabel: p.gstin })),
    [parties]);

  const balBadge = (n) => n >= 0
    ? <span className="text-blue-600 font-semibold">{fmtINR(Math.abs(n))} Dr</span>
    : <span className="text-emerald-600 font-semibold">{fmtINR(Math.abs(n))} Cr</span>;

  return (
    <div className="space-y-4">
      <div className="card-sharp p-4 grid md:grid-cols-4 gap-3">
        <div>
          <Label>Party</Label>
          <SearchSelect options={partyOptions} value={partyId} onChange={setPartyId}
                          testId={`soa-party-${partyType}`}
                          placeholder={`Select ${label.toLowerCase()}`} />
        </div>
        <div>
          <Label>From date</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                    className="rounded-sm mt-1" data-testid={`soa-from-${partyType}`} />
        </div>
        <div>
          <Label>To date</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                    className="rounded-sm mt-1" data-testid={`soa-to-${partyType}`} />
        </div>
        <div className="flex items-end gap-2">
          <Button onClick={run} disabled={loading || !partyId}
                    className="rounded-sm bg-accent hover:bg-accent/90 text-white flex-1"
                    data-testid={`soa-run-${partyType}`}>
            {loading ? "Loading…" : "Run"}
          </Button>
          {statement && (
            <>
              <Button variant="outline" onClick={downloadPDF} className="rounded-sm"
                        data-testid={`soa-pdf-${partyType}`}>
                <Download className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={() => setEmailOpen(true)} className="rounded-sm"
                        data-testid={`soa-email-${partyType}`}>
                <Mail className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {statement && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="card-sharp p-3">
              <div className="text-[10px] uppercase font-mono text-muted-foreground">Opening</div>
              <div className="mt-1">{balBadge(statement.opening)}</div>
            </div>
            <div className="card-sharp p-3">
              <div className="text-[10px] uppercase font-mono text-muted-foreground">Total Debit</div>
              <div className="mt-1 font-semibold tabular-nums">{fmtINR(statement.totals.debit)}</div>
            </div>
            <div className="card-sharp p-3">
              <div className="text-[10px] uppercase font-mono text-muted-foreground">Total Credit</div>
              <div className="mt-1 font-semibold tabular-nums">{fmtINR(statement.totals.credit)}</div>
            </div>
            <div className="card-sharp p-3">
              <div className="text-[10px] uppercase font-mono text-muted-foreground">Closing</div>
              <div className="mt-1">{balBadge(statement.closing)}</div>
            </div>
          </div>

          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead>
                <tr>
                  <th>Date</th><th>Voucher</th><th>Type</th><th>Narration</th>
                  <th className="text-right">Debit</th>
                  <th className="text-right">Credit</th>
                  <th className="text-right">Running</th>
                </tr>
              </thead>
              <tbody data-testid={`soa-ledger-${partyType}`}>
                <tr className="bg-muted/40">
                  <td colSpan="3"></td>
                  <td className="font-semibold">Opening Balance</td>
                  <td colSpan="2"></td>
                  <td className="text-right tabular-nums font-semibold">{fmtINR(statement.opening)}</td>
                </tr>
                {statement.transactions.length === 0 && (
                  <tr><td colSpan="7" className="text-center text-muted-foreground py-4">No transactions in this window</td></tr>
                )}
                {statement.transactions.map((t, i) => (
                  <tr key={i}>
                    <td className="text-xs">{fmtDate(t.date)}</td>
                    <td className="font-mono text-xs">{t.voucher_number}</td>
                    <td><span className="text-[10px] uppercase font-mono text-accent">{t.voucher_type}</span></td>
                    <td className="text-xs">{t.narration}</td>
                    <td className="text-right tabular-nums">{t.debit ? fmtINR(t.debit) : ""}</td>
                    <td className="text-right tabular-nums text-muted-foreground">{t.credit ? fmtINR(t.credit) : ""}</td>
                    <td className="text-right tabular-nums font-medium">{fmtINR(t.running)}</td>
                  </tr>
                ))}
                <tr className="bg-black text-white">
                  <td colSpan="3"></td>
                  <td className="font-bold uppercase">Closing Balance</td>
                  <td className="text-right tabular-nums font-bold">{fmtINR(statement.totals.debit)}</td>
                  <td className="text-right tabular-nums font-bold">{fmtINR(statement.totals.credit)}</td>
                  <td className="text-right tabular-nums font-bold">{fmtINR(statement.closing)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-lg" data-testid={`soa-email-dialog-${partyType}`}>
          <DialogHeader><DialogTitle>Email Statement</DialogTitle></DialogHeader>
          <EmailForm partyId={partyId} fromDate={fromDate} toDate={toDate}
                       party={parties.find((p) => p.id === partyId)}
                       onDone={() => setEmailOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmailForm({ partyId, fromDate, toDate, party, onDone }) {
  const [recipient, setRecipient] = useState(party?.email || "");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState(`Statement of Account · ${fromDate} to ${toDate}`);
  const [message, setMessage] = useState(
    `Dear ${party?.name || "Sir/Madam"},\n\nPlease find attached your Statement of Account for the period ${fromDate} to ${toDate}.\n\nKindly review and let us know of any discrepancies.\n\nRegards,\nAccounts Team`);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const p = party?.email_prefs || {};
    setRecipient((p.to || []).join(", ") || party?.email || "");
    setCc((p.cc || []).join(", "));
    setBcc((p.bcc || []).join(", "));
  }, [party]);
  const list = (s) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

  const send = async () => {
    if (!recipient?.includes("@")) return toast.error("Enter recipient email");
    setBusy(true);
    try {
      const url = `${API}/reports/statement-of-account.pdf?party_id=${partyId}&from_date=${fromDate}&to_date=${toDate}`;
      const res = await fetch(url, { credentials: "include" });
      const blob = await res.blob();
      const b64 = await new Promise((r) => { const fr = new FileReader(); fr.onload = () => r(fr.result.split(",")[1]); fr.readAsDataURL(blob); });
      const r = await api.post("/email/send-report", {
        recipient, cc: list(cc), bcc: list(bcc), party_id: partyId, subject, message,
        report_label: "Statement of Account",
        pdf_base64: b64,
        pdf_filename: `Statement_${fromDate}_to_${toDate}.pdf`,
      });
      if (r.data.ok) { toast.success("Emailed"); onDone(); }
      else toast.error(r.data.error || "Send failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div><Label>To <span className="text-[10px] text-muted-foreground font-normal">(separate several with commas)</span></Label><Input value={recipient} onChange={(e) => setRecipient(e.target.value)} className="rounded-sm mt-1" data-testid="soa-email-to" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>CC</Label><Input value={cc} onChange={(e) => setCc(e.target.value)} className="rounded-sm mt-1" data-testid="soa-email-cc" /></div>
        <div><Label>BCC</Label><Input value={bcc} onChange={(e) => setBcc(e.target.value)} className="rounded-sm mt-1" data-testid="soa-email-bcc" /></div>
      </div>
      <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-sm mt-1" data-testid="soa-email-subject" /></div>
      <div><Label>Message</Label><Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="rounded-sm mt-1 min-h-[120px]" data-testid="soa-email-msg" /></div>
      <div className="text-[11px] italic text-muted-foreground">PDF statement is attached automatically. To / CC / BCC are remembered for this party — the same addresses are used for invoices and automatic reminders.</div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onDone} className="rounded-sm">Cancel</Button>
        <Button onClick={send} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="soa-email-send">{busy ? "Sending…" : "Send"}</Button>
      </div>
    </div>
  );
}

export default function StatementOfAccount() {
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="statement-of-account-page">
      <PageHeader
        eyebrow="Reports"
        title="Statement of Account"
        description="Ledger of every voucher touching a party's control account between two dates — opening, closing, PDF export, one-click email. Works for Customers, Vendors, and any other party (employees, related parties, owner)."
      />
      <Tabs defaultValue="customer">
        <TabsList className="rounded-sm">
          <TabsTrigger value="customer" className="rounded-sm" data-testid="soa-tab-customer">Customers</TabsTrigger>
          <TabsTrigger value="vendor" className="rounded-sm" data-testid="soa-tab-vendor">Vendors</TabsTrigger>
          <TabsTrigger value="other" className="rounded-sm" data-testid="soa-tab-other">Other Parties</TabsTrigger>
        </TabsList>
        <TabsContent value="customer" className="mt-4"><StatementForType partyType="customer" label="Customer" /></TabsContent>
        <TabsContent value="vendor" className="mt-4"><StatementForType partyType="vendor" label="Vendor" /></TabsContent>
        <TabsContent value="other" className="mt-4"><StatementForType partyType="other" label="Party" /></TabsContent>
      </Tabs>
    </div>
  );
}
